import * as THREE from 'three';
import { WEAPON_TYPES, WEAPONS, DEFAULT_WEAPON, DEFAULT_AMMO, getWeaponByKey } from '../weapons';
import { markRaw } from 'vue';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

class WeaponSystem {
  constructor(scene, camera, socket = null) {
    this.scene = scene;
    this.camera = camera;
    this.socket = socket; // Add socket reference for shooting events
    
    // Current active weapon
    this.currentWeapon = DEFAULT_WEAPON;
    
    // Weapon models
    this.weaponModels = {};
    
    // Hands model for first-person view
    this.handsModel = null;
    this.handsMixer = null;
    
    // Store animations for different weapons
    this.handsAnimations = {
      global: {}, // Global animations for all weapons
      [WEAPON_TYPES.KNIFE]: {}, // Knife-specific animations
      [WEAPON_TYPES.PISTOL]: {}, // Pistol-specific animations 
      [WEAPON_TYPES.MACHINE_GUN]: {} // Rifle-specific animations
    };
    
    // Player ammo state
    this.ammo = { ...DEFAULT_AMMO }; // Clone to avoid modifying default
    
    // Local player ID (to ignore when shooting)
    this.localPlayerId = socket ? socket.id : 'local-player';
    
    // Weapon state
    this.state = {
      isReloading: false,
      isSwitching: false,
      isShooting: false,
      lastShotTime: 0,
      switchStartTime: 0
    };
    
    // GLTF loader for gun models
    this.gltfLoader = markRaw(new GLTFLoader());
    
    // Cache for loaded models
    this.modelCache = {};
    
    // For tracking weapons attached to hands
    this.attachedWeaponUpdateFunctions = [];
    
    // Recoil system
    this.recoilState = {
      current: 0,      // Current recoil amount
      max: 0.1,        // Maximum recoil amount
      recovery: 0.01,  // How quickly recoil recovers per frame
      lastUpdate: 0,   // Last time the recoil was updated
      shotsFired: 0,   // Track how many shots have been fired
      timeSinceLastShot: 0, // Time since last shot for reset tracking
    };
    
    // Hit detection statistics
    this.hitStats = {
      totalShots: 0,
      hits: 0,
      misses: 0,
      hitsByBodyPart: {
        head: 0,
        torso: 0,
        limb: 0,
        hand: 0,
        default: 0
      },
      hitsByWeapon: {
        [WEAPON_TYPES.KNIFE]: 0,
        [WEAPON_TYPES.PISTOL]: 0,
        [WEAPON_TYPES.MACHINE_GUN]: 0
      },
      averageTotalTime: 0,
      averageTraverseTime: 0,
      averageIntersectTime: 0,
      averageProcessTime: 0,
      lastTotalTimes: [], // Store the last 10 timings for average calculation
      lastTraverseTimes: [],
      lastIntersectTimes: [],
      lastProcessTimes: [],
      objectsInScene: 0,
      potentialTargets: 0
    };

    // Target caching for hit detection optimization
    this.targetCache = {
      targets: [],                 // Cached list of potential targets
      lastUpdateTime: 0,           // Time when the cache was last updated
      updateInterval: 500,         // How often to update the cache (ms)
      enabled: true,               // Whether to use the target cache
      cachedCount: 0,              // How many objects are in the cache
      sceneVersion: 0,             // Version number to track scene changes
      hitsMissedDueToStaleCache: 0 // Counter for hits that might have been missed due to stale cache
    };
    
    // Sound system for weapon sounds
    this.soundSystem = {
      enabled: true,               // Master toggle for weapon sounds
      volume: 0.7,                 // Master volume (0.0 to 1.0)
      sounds: {},                  // Sound object cache
      shootingMode: {              // For tracking auto-fire state
        isAutoFiring: false,       // Is the machine gun currently auto-firing?
        autoFireInterval: null,    // Interval ID for auto-fire loop
      }
    };
    
    // Create weapon models
    this.createWeaponModels();
    
    // Load hands model
    this.loadHandsModel();
    
    // Initialize sound system
    this.initSoundSystem();
  }
  
  // Initialize sound system
  initSoundSystem() {
    // Define sound paths for different weapons and actions
    const soundPaths = {
      // Knife sounds
      [`${WEAPON_TYPES.KNIFE}_swing`]: '/sounds/weapons/knife_swing.mp3',
      [`${WEAPON_TYPES.KNIFE}_hit`]: '/sounds/weapons/knife_hit.mp3',
      
      // Pistol sounds
      [`${WEAPON_TYPES.PISTOL}_shoot`]: '/sounds/weapons/pistol_shot.mp3',
      [`${WEAPON_TYPES.PISTOL}_reload`]: '/sounds/weapons/pistol_reload.mp3',
      [`${WEAPON_TYPES.PISTOL}_empty`]: '/sounds/weapons/gun_empty.mp3',
      
      // Machine gun sounds
      [`${WEAPON_TYPES.MACHINE_GUN}_shoot`]: '/sounds/weapons/rifle_shot.mp3',
      [`${WEAPON_TYPES.MACHINE_GUN}_reload`]: '/sounds/weapons/rifle_reload.mp3',
      [`${WEAPON_TYPES.MACHINE_GUN}_empty`]: '/sounds/weapons/gun_empty.mp3',
      
      // Generic sounds
      'weapon_switch': '/sounds/weapons/weapon_switch.mp3',
      
      // Hit sounds
      'hit_body': '/sounds/impacts/hit_body.mp3',
      'hit_head': '/sounds/impacts/hit_head.mp3',
      'hit_limb': '/sounds/impacts/hit_limb.mp3',
      'hit_object': '/sounds/impacts/hit_object.mp3'
    };
    
    // Preload all sounds
    Object.entries(soundPaths).forEach(([key, path]) => {
      this.preloadSound(key, path);
    });
    
    console.log('Weapon sound system initialized');
  }
  
  // Preload a sound and cache it
  preloadSound(soundKey, soundPath) {
    try {
      // Create an audio element
      const audio = new Audio(soundPath);
      
      // Set properties
      audio.volume = this.soundSystem.volume;
      audio.preload = 'auto';
      
      // Store in cache
      this.soundSystem.sounds[soundKey] = audio;
      
      // Handle loading
      audio.addEventListener('canplaythrough', () => {
        console.log(`Sound loaded: ${soundKey}`);
      });
      
      audio.addEventListener('error', (error) => {
        console.warn(`Error loading sound ${soundKey}: ${error}`);
      });
      
      // Start loading
      audio.load();
      
    } catch (error) {
      console.error(`Failed to preload sound ${soundKey}: ${error}`);
    }
  }
  
  // Play a sound with optional parameters
  playSound(soundKey, options = {}) {
    // Skip if sound system is disabled
    if (!this.soundSystem.enabled) return;
    
    // Default options
    const defaultOptions = {
      volume: this.soundSystem.volume, // Use master volume by default
      loop: false,                     // No looping by default
      rate: 1.0,                       // Normal playback rate
      detune: 0,                       // No pitch shift
      delay: 0                         // No delay
    };
    
    // Merge options
    const finalOptions = { ...defaultOptions, ...options };
    
    // Get the sound from cache
    const sound = this.soundSystem.sounds[soundKey];
    
    // If sound doesn't exist or failed to load, try to create it on-the-fly
    if (!sound) {
      console.warn(`Sound ${soundKey} not found in cache`);
      return;
    }
    
    try {
      // For non-looping sounds, it's better to create a new instance
      // to allow for overlapping sounds (like rapid gunfire)
      const soundInstance = sound.cloneNode();
      
      // Apply options
      soundInstance.volume = finalOptions.volume;
      soundInstance.loop = finalOptions.loop;
      soundInstance.playbackRate = finalOptions.rate;
      
      // Apply slight randomization to make repeated sounds more natural
      if (finalOptions.randomizePitch) {
        // Add a small random variation to playback rate (-0.1 to +0.1)
        soundInstance.playbackRate += (Math.random() * 0.2) - 0.1;
      }
      
      // Add a random volume variation if requested
      if (finalOptions.randomizeVolume) {
        // Add a small random variation to volume (-10% to +10%)
        const volumeVariation = (Math.random() * 0.2) - 0.1;
        soundInstance.volume = Math.max(0, Math.min(1, soundInstance.volume + volumeVariation));
      }
      
      // Handle delay
      if (finalOptions.delay > 0) {
        setTimeout(() => {
          soundInstance.play().catch(e => console.warn('Error playing sound:', e));
        }, finalOptions.delay);
      } else {
        // Play immediately
        soundInstance.play().catch(e => console.warn('Error playing sound:', e));
      }
      
      // Clean up when finished
      soundInstance.addEventListener('ended', () => {
        soundInstance.remove();
      });
      
    } catch (error) {
      console.error(`Error playing sound ${soundKey}: ${error}`);
    }
  }
  
  // Play spatial sound based on position in 3D space
  playSpatialSound(soundKey, position, options = {}) {
    // Skip if sound system is disabled
    if (!this.soundSystem.enabled) return;
    
    // Get base options
    const defaultOptions = {
      volume: this.soundSystem.volume,
      loop: false,
      rate: 1.0,
      randomizePitch: true,
      randomizeVolume: true,
      maxDistance: 50, // Maximum distance at which sound is audible
      refDistance: 10, // Distance at which volume starts to drop off
      rolloffFactor: 1.5 // How quickly sound attenuates with distance
    };
    
    // Merge options
    const finalOptions = { ...defaultOptions, ...options };
    
    // Get the sound from cache
    const sound = this.soundSystem.sounds[soundKey];
    if (!sound) {
      console.warn(`Sound ${soundKey} not found in cache`);
      return;
    }
    
    try {
      // Create a new instance to allow for overlapping sounds
      const soundInstance = sound.cloneNode();
      
      // Apply base options
      soundInstance.loop = finalOptions.loop;
      soundInstance.playbackRate = finalOptions.rate;
      
      // Apply randomization if requested
      if (finalOptions.randomizePitch) {
        soundInstance.playbackRate += (Math.random() * 0.2) - 0.1;
      }
      
      // Calculate distance from camera/listener
      const cameraPosition = this.camera.position;
      const distance = new THREE.Vector3(
        position.x - cameraPosition.x,
        position.y - cameraPosition.y,
        position.z - cameraPosition.z
      ).length();
      
      // Apply distance-based volume attenuation (inverse square law with rolloff)
      let volume = finalOptions.volume;
      if (distance > finalOptions.refDistance) {
        // Calculate attenuation factor
        const attenuationFactor = finalOptions.refDistance / 
          (finalOptions.refDistance + finalOptions.rolloffFactor * 
            (Math.min(distance, finalOptions.maxDistance) - finalOptions.refDistance));
        
        // Apply attenuation
        volume *= attenuationFactor;
      }
      
      // Add random variation if requested
      if (finalOptions.randomizeVolume) {
        const volumeVariation = (Math.random() * 0.2) - 0.1;
        volume = Math.max(0, Math.min(1, volume + volumeVariation));
      }
      
      // Apply final volume
      soundInstance.volume = volume;
      
      // Calculate stereo panning based on position relative to camera
      // Convert world positions to camera space
      const cameraDirection = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
      const toSound = new THREE.Vector3(
        position.x - cameraPosition.x,
        position.y - cameraPosition.y,
        position.z - cameraPosition.z
      ).normalize();
      
      // Cross product gives us vector perpendicular to both (left/right direction)
      const right = new THREE.Vector3().crossVectors(cameraDirection, new THREE.Vector3(0, 1, 0)).normalize();
      
      // Dot product with right vector gives us left/right position (-1 to 1)
      const pan = right.dot(toSound);
      
      // Create a stereo panner if supported
      if (window.AudioContext && window.StereoPannerNode) {
        try {
          // Use Web Audio API for more sophisticated spatial audio if available
          if (!this.soundSystem.audioContext) {
            this.soundSystem.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
          
          const audioContext = this.soundSystem.audioContext;
          const source = audioContext.createMediaElementSource(soundInstance);
          const panner = audioContext.createStereoPanner();
          
          // Apply pan value (-1 is full left, 1 is full right)
          panner.pan.value = Math.max(-1, Math.min(1, pan));
          
          // Connect nodes and play
          source.connect(panner);
          panner.connect(audioContext.destination);
          soundInstance.play().catch(e => console.warn('Error playing spatial sound:', e));
          
          // Store nodes for cleanup
          const nodes = { source, panner };
          
          // Clean up when finished
          soundInstance.addEventListener('ended', () => {
            // Disconnect nodes
            source.disconnect();
            panner.disconnect();
            soundInstance.remove();
          });
          
          return;
        } catch (e) {
          console.warn('Failed to use Web Audio API for spatial audio, falling back to basic audio:', e);
        }
      }
      
      // Fallback to basic panning if Web Audio API setup fails
      // HTML5 Audio allows simple left/right balance via stereo panning
      // Some browsers support the Audio's 'pan' property
      if ('pan' in soundInstance) {
        soundInstance.pan = pan;
      }
      
      // Play the sound
      soundInstance.play().catch(e => console.warn('Error playing spatial sound:', e));
      
      // Clean up when finished
      soundInstance.addEventListener('ended', () => {
        soundInstance.remove();
      });
      
    } catch (error) {
      console.error(`Error playing spatial sound ${soundKey}: ${error}`);
    }
  }
  
  // Stop a specific sound
  stopSound(soundKey) {
    const sound = this.soundSystem.sounds[soundKey];
    if (sound) {
      sound.pause();
      sound.currentTime = 0;
    }
  }
  
  // Load a GLTF/GLB model and store in cache
  loadModel(modelPath) {
    // Return from cache if already loaded
    if (this.modelCache[modelPath]) {
      return Promise.resolve(this.modelCache[modelPath]);
    }
    
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        modelPath,
        (gltf) => {
          // Mark the model as non-reactive (for Vue)
          const model = markRaw(gltf);
          
          // Setup model for shadowing
          model.scene.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Apply polygon offset to fix shadows when objects intersect with terrain
              if (child.material) {
                // Ensure we have a unique material instance
                if (!child.material._isCloned) {
                  if (Array.isArray(child.material)) {
                    child.material = child.material.map(m => m.clone());
                    child.material.forEach(m => m._isCloned = true);
                  } else {
                    child.material = child.material.clone();
                    child.material._isCloned = true;
                  }
                }
              }
            }
          });
          
          // Store in cache
          this.modelCache[modelPath] = model;
          console.log(`Weapon model loaded and cached: ${modelPath}`);
          resolve(model);
        },
        (xhr) => {
          console.log(`${modelPath} loading: ${(xhr.loaded / xhr.total * 100).toFixed(2)}% loaded`);
        },
        (error) => {
          console.error(`Error loading model ${modelPath}:`, error);
          reject(error);
        }
      );
    });
  }
  
  // Load the hands model for first-person view
  loadHandsModel() {
    const handsModelPath = '/models/players/hands.glb';
    
    this.loadModel(handsModelPath)
      .then(handsGltf => {
        console.log('Hands model loaded successfully:', handsGltf);
        
        // Create a container for the hands
        const handsContainer = markRaw(new THREE.Group());
        
        // Add the hands model to the container
        handsContainer.add(handsGltf.scene);
        
        // Initial scale for the hands - increased significantly
        handsGltf.scene.scale.set(3.0, 3.0, 3.0);
        
        // Rotate the hands to match FPV perspective
        handsGltf.scene.rotation.set(0, Math.PI, 0); // Rotate 180 degrees around Y axis
        
        // Store reference
        this.handsModel = handsContainer;
        
        // Tag the hands model to prevent frustum culling
        handsContainer.userData.isWeaponHands = true;
        handsContainer.userData.alwaysVisible = true;
        
        // Add to scene initially (will be positioned in update method)
        this.scene.add(handsContainer);
        
        // Set up animations if available
        if (handsGltf.animations && handsGltf.animations.length > 0) {
          console.log(`Hands model has ${handsGltf.animations.length} animations:`, 
                     handsGltf.animations.map(a => a.name).join(', '));
          
          // Create a mixer for the hands animations
          this.handsMixer = markRaw(new THREE.AnimationMixer(handsGltf.scene));
          
          // Categorize animations by weapon type and action based on naming conventions
          handsGltf.animations.forEach(animation => {
            const action = this.handsMixer.clipAction(animation);
            const animName = animation.name;
            
            // Determine which weapon this animation belongs to
            let targetWeapon = null;
            
            if (animName.includes('knife') || animName.includes('melee')) {
              targetWeapon = WEAPON_TYPES.KNIFE;
            } else if (animName.includes('pistol') || animName.includes('handgun')) {
              targetWeapon = WEAPON_TYPES.PISTOL;
            } else if (animName.includes('rifle') || animName.includes('ak') || animName.includes('machine')) {
              targetWeapon = WEAPON_TYPES.MACHINE_GUN;
            } else {
              // If no specific weapon mentioned, consider it a global animation
              targetWeapon = 'global';
            }
            
            // Store the animation in the appropriate category
            if (!this.handsAnimations[targetWeapon]) {
              this.handsAnimations[targetWeapon] = {};
            }
            
            // Store animation by its original name
            this.handsAnimations[targetWeapon][animName] = action;
            
            // Map to common action names based on animation name
            if (animName.includes('idle')) {
              this.handsAnimations[targetWeapon]['idle'] = action;
            } else if (animName.includes('shoot') || animName.includes('fire')) {
              this.handsAnimations[targetWeapon]['shoot'] = action;
            } else if (animName.includes('reload')) {
              this.handsAnimations[targetWeapon]['reload'] = action;
            } else if (animName.includes('attack') || animName.includes('swing')) {
              this.handsAnimations[targetWeapon]['attack'] = action;
            } else if (animName.includes('run') || animName.includes('walk')) {
              if (animName.includes('fwd')) {
                this.handsAnimations[targetWeapon]['walk'] = action;
                this.handsAnimations[targetWeapon]['run'] = action;
              } else if (animName.includes('bwd')) {
                this.handsAnimations[targetWeapon]['walkBackward'] = action;
              } else if (animName.includes('left')) {
                this.handsAnimations[targetWeapon]['walkLeft'] = action;
              } else if (animName.includes('right')) {
                this.handsAnimations[targetWeapon]['walkRight'] = action;
              }
            }
          });
          
          console.log('Categorized hand animations by weapon type:', 
                     Object.keys(this.handsAnimations).map(key => 
                       `${key}: ${Object.keys(this.handsAnimations[key]).length} animations`
                     ).join(', '));
          
          // Start idle animation for current weapon
          this.playWeaponAnimation('idle');
        }
        
        // Make sure the hands are visible with enhanced materials
        handsGltf.scene.traverse(child => {
          if (child.isMesh) {
            child.visible = true;
            child.frustumCulled = false; // Prevent hands from being culled by the camera
            child.renderOrder = 1; // Ensure hands render on top of other objects
            
            // Mark each mesh individually
            child.userData.isWeaponHands = true;
            child.userData.alwaysVisible = true;
            
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  mat.transparent = false;
                  mat.opacity = 1.0;
                  mat.depthTest = true;
                  mat.depthWrite = true;
                  mat.needsUpdate = true;
                });
              } else {
                child.material.transparent = false;
                child.material.opacity = 1.0;
                child.material.depthTest = true;
                child.material.depthWrite = true;
                child.material.needsUpdate = true;
              }
            }
          }
        });
        
        // Adjust hands for the current weapon
        this.adjustHandsForWeapon(this.currentWeapon);
        
        console.log('Hands model ready for positioning');
      })
      .catch(error => {
        console.error('Failed to load hands model:', error);
      });
  }
  
  // Create weapon models
  createWeaponModels() {
    // We'll load weapon models from external files, but keep the simple geometric models as fallbacks
    
    // Initial geometric fallback models
    //this.weaponModels[WEAPON_TYPES.KNIFE] = this.createKnifeModel();
    //this.weaponModels[WEAPON_TYPES.PISTOL] = this.createPistolModel();
   // this.weaponModels[WEAPON_TYPES.MACHINE_GUN] = this.createMachineGunModel();
    
    // Add fallback models to scene but hide them initially
    Object.values(this.weaponModels).forEach(model => {
      this.scene.add(model);
      model.visible = false;
    });
    
    // Show the default weapon
    this.showWeapon(this.currentWeapon);
    
    // External model paths
    const modelPaths = {
      [WEAPON_TYPES.KNIFE]: '/models/knife.glb',
      [WEAPON_TYPES.PISTOL]: '/models/pistol.glb',
      [WEAPON_TYPES.MACHINE_GUN]: '/models/ak.glb'
    };
    
    // Load external models (these will be used for attached models)
    Object.entries(modelPaths).forEach(([weaponType, path]) => {
      this.loadModel(path).catch(error => {
        console.warn(`Failed to preload weapon model ${path}, will use fallback: ${error}`);
      });
    });
  }
  
  // Load the rifle model from GLB file
  async loadRifleModel() {
    try {
      const rifleGltf = await this.loadModel('/models/ak.glb');
      const rifleModel = markRaw(new THREE.Group());
      
      // Add the rifle model to our group
      rifleModel.add(rifleGltf.scene);
      
      // Position and rotate according to weapon config
      const config = WEAPONS[WEAPON_TYPES.MACHINE_GUN].model;
      rifleModel.scale.set(config.scale * 0.75, config.scale * 0.75, config.scale * 0.75); // Scale for FPV
      rifleModel.position.set(
        config.position.x, 
        config.position.y, 
        config.position.z
      );
      rifleModel.rotation.set(
        config.rotation.x,
        config.rotation.y-Math.PI,
        config.rotation.z
      );
      
      console.log("Rifle model loaded successfully");
      return rifleModel;
    } catch (error) {
      console.error("Error loading rifle model:", error);
      throw error;
    }
  }
  
  // Create a simple knife model
  createKnifeModel() {
    const knifeGroup = markRaw(new THREE.Group());
    
    // Blade
    const bladeGeometry = new THREE.BoxGeometry(0.05, 0.5, 0.1);
    const bladeMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xcccccc, 
      metalness: 0.9,
      roughness: 0.2
    });
    const blade = new THREE.Mesh(bladeGeometry, bladeMaterial);
    blade.position.y = 0.3;
    
    // Handle
    const handleGeometry = new THREE.BoxGeometry(0.08, 0.2, 0.12);
    const handleMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x333333, 
      metalness: 0.2,
      roughness: 0.8
    });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    handle.position.y = 0.05;
    
    // Add blade and handle to knife group
    knifeGroup.add(blade);
    knifeGroup.add(handle);
    
    // Position and rotate according to weapon config
    const config = WEAPONS[WEAPON_TYPES.KNIFE].model;
    knifeGroup.scale.set(config.scale, config.scale, config.scale);
    knifeGroup.position.set(
      config.position.x, 
      config.position.y, 
      config.position.z
    );
    knifeGroup.rotation.set(
      config.rotation.x,
      config.rotation.y,
      config.rotation.z
    );
    
    return knifeGroup;
  }
  
  // Create a simple pistol model
  createPistolModel() {
    const pistolGroup = markRaw(new THREE.Group());
    
    // Gun body
    const bodyGeometry = new THREE.BoxGeometry(0.1, 0.15, 0.4);
    const bodyMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x222222, 
      metalness: 0.7,
      roughness: 0.3
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    
    // Barrel
    const barrelGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.2, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x111111, 
      metalness: 0.9,
      roughness: 0.1
    });
    const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -0.3;
    
    // Handle
    const handleGeometry = new THREE.BoxGeometry(0.08, 0.2, 0.1);
    const handleMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x111111, 
      metalness: 0.2,
      roughness: 0.8
    });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    handle.position.y = -0.17;
    handle.position.z = 0.05;
    
    // Add parts to pistol group
    pistolGroup.add(body);
    pistolGroup.add(barrel);
    pistolGroup.add(handle);
    
    // Position and rotate according to weapon config
    const config = WEAPONS[WEAPON_TYPES.PISTOL].model;
    pistolGroup.scale.set(config.scale, config.scale, config.scale);
    pistolGroup.position.set(
      config.position.x, 
      config.position.y, 
      config.position.z
    );
    pistolGroup.rotation.set(
      config.rotation.x,
      config.rotation.y,
      config.rotation.z
    );
    
    return pistolGroup;
  }
  
  // Create a simple machine gun model (fallback if GLB loading fails)
  createMachineGunModel() {
    const gunGroup = markRaw(new THREE.Group());
    
    // Main body
    const bodyGeometry = new THREE.BoxGeometry(0.1, 0.15, 0.7);
    const bodyMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x333333, 
      metalness: 0.7,
      roughness: 0.3
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    
    // Barrel
    const barrelGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.4, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x111111, 
      metalness: 0.9,
      roughness: 0.2
    });
    const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -0.55;
    
    // Handle
    const handleGeometry = new THREE.BoxGeometry(0.08, 0.2, 0.1);
    const handleMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x111111, 
      metalness: 0.2,
      roughness: 0.8
    });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    handle.position.y = -0.17;
    handle.position.z = 0.1;
    
    // Magazine
    const magGeometry = new THREE.BoxGeometry(0.08, 0.25, 0.1);
    const magMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x444444, 
      metalness: 0.6,
      roughness: 0.4
    });
    const magazine = new THREE.Mesh(magGeometry, magMaterial);
    magazine.position.y = -0.2;
    magazine.position.z = -0.1;
    
    // Add parts to gun group
    gunGroup.add(body);
    gunGroup.add(barrel);
    gunGroup.add(handle);
    gunGroup.add(magazine);
    
    // Position and rotate according to weapon config
    const config = WEAPONS[WEAPON_TYPES.MACHINE_GUN].model;
    gunGroup.scale.set(config.scale, config.scale, config.scale);
    gunGroup.position.set(
      config.position.x, 
      config.position.y, 
      config.position.z
    );
    gunGroup.rotation.set(
      config.rotation.x,
      config.rotation.y,
      config.rotation.z
    );
    
    return gunGroup;
  }
  
  // Update showWeapon method to also control hands visibility
  showWeapon(weaponType) {
    // Show the specified weapon and hide others
    Object.entries(this.weaponModels).forEach(([type, model]) => {
      if (model) {
        model.visible = (type === weaponType);
      }
    });
    
    // Update hands visibility to match the selected weapon
    if (this.handsModel) {
      // If weaponType is null, hide hands (during weapon switching)
      // Otherwise show hands when a weapon is visible
      this.handsModel.visible = (weaponType !== null);
    }
  }
  
  // Switch to a different weapon
  switchWeapon(weaponType) {
    // Check if we're already switching weapons
    if (this.state.isSwitching) {
      return false;
    }
    
    // Check if the requested weapon is already active
    if (weaponType === this.currentWeapon) {
      return false;
    }
    
    // Check if the weapon type is valid
    if (!WEAPONS[weaponType]) {
      console.warn(`Invalid weapon type: ${weaponType}`);
      return false;
    }
    
    // Start weapon switch
    this.state.isSwitching = true;
    this.state.switchStartTime = Date.now();
    
    // Hide current weapon
    this.showWeapon(null);
    
    // Play weapon switch sound
    this.playSound('weapon_switch', {
      volume: 0.6,
      randomizePitch: true
    });
    
    // Get switch time from weapon config
    const switchTime = WEAPONS[weaponType].switchTime * 1000; // Convert to ms
    
    // Schedule switch completion
    setTimeout(() => {
      this.currentWeapon = weaponType;
      this.showWeapon(weaponType);
      this.state.isSwitching = false;
      
      // Adjust hands model based on weapon type
      this.adjustHandsForWeapon(weaponType);
      
      // Play the idle animation for the new weapon
      this.playWeaponAnimation('idle');
      
      console.log(`Switched to ${WEAPONS[weaponType].name}`);
      
      // Call the onWeaponSwitch callback if defined
      if (typeof this.onWeaponSwitch === 'function') {
        this.onWeaponSwitch(weaponType);
      }
      
      // Stop auto-firing if we were doing that
      this.stopAutoFire();
    }, switchTime);
    
    return true;
  }
  
  // Switch weapon by key number (1, 2, 3)
  switchWeaponByKey(keyNumber) {
    const weaponType = getWeaponByKey(keyNumber);
    if (weaponType) {
      return this.switchWeapon(weaponType);
    }
    return false;
  }
  
  // Get current ammo for active weapon
  getCurrentAmmo() {
    return this.ammo[this.currentWeapon];
  }
  
  // Get fire rate for current weapon
  getFireRate() {
    return WEAPONS[this.currentWeapon]?.fireRate || 1;
  }
  
  // Check if can shoot with current weapon
  canShoot() {
    // Can't shoot while switching weapons
    if (this.state.isSwitching) {
      return false;
    }
    
    // Can't shoot while reloading
    if (this.state.isReloading) {
      return false;
    }
    
    // Check if player is dead
    if (this.isPlayerDead) {
      return false;
    }
    
    // Check fire rate limit
    const now = Date.now();
    const timeSinceLastShot = now - this.state.lastShotTime;
    const minTimeBetweenShots = 1000 / WEAPONS[this.currentWeapon].fireRate;
    
    if (timeSinceLastShot < minTimeBetweenShots) {
      return false;
    }
    
    // Check if we have ammo
    if (this.ammo[this.currentWeapon] <= 0) {
      // Trigger reload if no ammo
      this.reload();
      return false;
    }
    
    return true;
  }
  
  // Method to set player death state
  setPlayerDeathState(isDead) {
    this.isPlayerDead = isDead;
    
    if (isDead) {
      this.playDeathAnimation();
    } else {
      this.resetDeathAnimation();
    }
  }
  
  // Play death animation for hands in first-person view
  playDeathAnimation() {
    if (!this.handsModel) return;
    
    // Mark as dead
    this.isPlayerDead = true;
    
    // Use death animation if available
    const deathAnimationName = this.findSuitableDeathAnimation();
    if (deathAnimationName) {
      this.playWeaponAnimation(deathAnimationName, {
        loop: false,
        timeScale: 0.8,
        clampWhenFinished: true
      });
    }
    
    // Store original positions to restore on respawn
    this.originalHandsPosition = {
      position: this.handsModel.position.clone(),
      rotation: this.handsModel.rotation.clone()
    };
    
    // Flag to track death animation
    this.isDeathAnimationPlaying = true;
    this.deathAnimationStartTime = Date.now();
  }
  
  // Find a suitable death animation for hands
  findSuitableDeathAnimation() {
    // Look for death animations in all weapon types
    for (const weaponType in this.handsAnimations) {
      const animations = this.handsAnimations[weaponType];
      // Check for any animation with "death" in its name
      for (const animName in animations) {
        if (animName.toLowerCase().includes('death')) {
          return animName;
        }
      }
    }
    // Fallback to a simple idle animation
    return 'idle';
  }
  
  // Reset from death animation when respawning
  resetDeathAnimation() {
    if (!this.handsModel) return;
    
    // Clear death state
    this.isPlayerDead = false;
    this.isDeathAnimationPlaying = false;
    
    // Reset hands to original position if stored
    if (this.originalHandsPosition) {
      // Don't instantly snap back, will be handled by the normal update
      // Position will be recalculated in the next update call
      this.playWeaponAnimation('idle');
    }
  }
  
  // Check if socket is available and connected
  checkSocketConnectivity() {
    // Only run this check if we're in a networked game (not local)
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (!this.socket) {
        console.error('Socket reference is missing in WeaponSystem. This should not happen in a networked game.');
        
        // Show an alert to the user
        if (confirm('Connection to game server lost. Reload the page to reconnect?')) {
          window.location.reload();
        }
        return false;
      }
      
      if (!this.socket.connected) {
        console.error('Socket exists but is disconnected. Attempting to reconnect...');
        
        // Try to reconnect
        this.socket.connect();
        
        // Return false to prevent shooting while disconnected
        return false;
      }
    }
    
    return true;
  }
  
  // Shoot the current weapon
  shoot() {
    if (!this.canShoot()) {
      // If we have a weapon but no ammo, play empty sound
      if (this.ammo[this.currentWeapon] <= 0 && !WEAPONS[this.currentWeapon].isMelee) {
        this.playSound(`${this.currentWeapon}_empty`, {
          volume: 0.5,
          randomizePitch: true
        });
      }
      return false;
    }
    
    // Check socket connectivity before shooting (in networked games)
    if (!this.checkSocketConnectivity()) {
      console.warn('Shooting aborted due to socket connectivity issues');
      return false;
    }
    
    const weapon = WEAPONS[this.currentWeapon];
    
    // Update last shot time
    const now = Date.now();
    // Check if it's been more than 1 second since last shot to reset shotsFired counter
    if (now - this.state.lastShotTime > 1000) {
      this.recoilState.shotsFired = 0;
    }
    this.state.lastShotTime = now;
    
    // Add recoil for non-melee weapons (skip for the first shot)
    if (!weapon.isMelee) {
      // Increment shots fired counter
      this.recoilState.shotsFired++;
      
      // Only apply recoil after first shot
      if (this.recoilState.shotsFired > 1) {
        // Different recoil per weapon type with reduced values
        if (this.currentWeapon === WEAPON_TYPES.PISTOL) {
          // Pistol adds moderate recoil per shot, but resets quickly
          this.recoilState.current += 0.01; // Reduced from 0.025
          this.recoilState.max = 0.03; // Reduced from 0.07
          this.recoilState.recovery = 0.015; // Increased recovery
        } 
        else if (this.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
          // Machine gun adds less recoil per shot but accumulates and recovers slower
          this.recoilState.current += 0.009; // Reduced from 0.02
          this.recoilState.max = 0.15; // Reduced from 0.2
          this.recoilState.recovery = 0.015; // Increased recovery
        }
        
        // Cap the recoil to the maximum amount
        if (this.recoilState.current > this.recoilState.max) {
          this.recoilState.current = this.recoilState.max;
        }
      }
      
      // Reset the last update time
      this.recoilState.lastUpdate = Date.now();
      
      // Reduce ammo
      this.ammo[this.currentWeapon]--;
    }
    
    // Create a ray for hit detection with recoil applied
    const ray = this.createRay();
    
    // Apply weapon-specific shooting effects and sounds
    let shotResult = null;
    if (this.currentWeapon === WEAPON_TYPES.KNIFE) {
      this.animateMeleeAttack();
      // Play knife swing sound
      this.playSound(`${WEAPON_TYPES.KNIFE}_swing`, {
        volume: 0.6,
        randomizePitch: true
      });
      // Melee attack has short range
      shotResult = this.handleMeleeAttack(ray, weapon);
    } else if (this.currentWeapon === WEAPON_TYPES.PISTOL) {
      this.animateGunShot();
      // Play pistol shot sound
      this.playSound(`${WEAPON_TYPES.PISTOL}_shoot`, {
        volume: 0.8,
        randomizePitch: true
      });
      // Single shot for pistol
      shotResult = this.handleGunShot(ray, weapon);
    } else if (this.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
      this.animateGunShot();
      // Play rifle shot sound
      this.playSound(`${WEAPON_TYPES.MACHINE_GUN}_shoot`, {
        volume: 0.9,
        randomizePitch: true
      });
      // Single shot for machine gun (shot rate is controlled by fire rate)
      shotResult = this.handleGunShot(ray, weapon);
      
      // Handle automatic fire for machine gun if applicable
      if (weapon.automatic && !this.soundSystem.shootingMode.isAutoFiring) {
        this.startAutoFire();
      }
    }
    
    // Play hit sound if we hit something
    if (shotResult && shotResult.hit) {
      // Determine which hit sound to play based on what was hit
      let hitSoundKey = 'hit_object';
      
      if (shotResult.hit.isPlayer) {
        // Play different sounds based on body part hit
        switch(shotResult.bodyPart) {
          case 'head':
            hitSoundKey = 'hit_head';
            break;
          case 'torso':
            hitSoundKey = 'hit_body';
            break;
          default:
            hitSoundKey = 'hit_limb';
            break;
        }
        
        // Add a slight delay to hit sounds to make it feel more natural
        this.playSound(hitSoundKey, {
          volume: 0.7,
          randomizePitch: true,
          delay: shotResult.hit.distance * 5 // Delay based on distance
        });
      } else {
        // For non-player objects
        this.playSound('hit_object', {
          volume: 0.5,
          randomizePitch: true,
          delay: shotResult.hit.distance * 5
        });
      }
    }
    
    // Send shooting event to server if socket is available and connected
    if (this.socket) {
      // Verify socket connection
      if (!this.socket.connected) {
        console.error('Cannot send shot to server: Socket exists but is not connected');
        // Continue with local shot processing but skip server communication
      } else {
        console.log('Socket is connected, sending shot data to server');
        
        // Get camera direction for shot direction
        const direction = new THREE.Vector3(0, 0, -1);
        direction.applyQuaternion(this.camera.quaternion);
        
        // Prepare shooting data with proper serialization
        const shootingData = {
          weaponType: this.currentWeapon,
          position: {
            x: this.camera.position.x,
            y: this.camera.position.y,
            z: this.camera.position.z
          },
          direction: {
            x: direction.x,
            y: direction.y,
            z: direction.z
          },
          timestamp: Date.now()
        };
        
        // Get hit information if any
        if (shotResult && shotResult.hit) {
          // Compatible hit data for the original server implementation
          const hitData = {
            position: {
              x: shotResult.hit.position.x,
              y: shotResult.hit.position.y,
              z: shotResult.hit.position.z
            },
            // Support existing server implementations by including original damage
            damage: weapon.damage, // Send base weapon damage for backward compatibility
            damageMultiplied: shotResult.damage, // Send final calculated damage with multiplier
            objectId: shotResult.hit.playerId || null,
            objectType: shotResult.hit.isPlayer ? 'player' : 'object'
          };
          
          // Add extended hit data for servers that support it
          hitData.bodyPart = shotResult.bodyPart || 'default';
          hitData.damageMultiplier = shotResult.hit.damageMultiplier || 1.0;
          hitData.playerId = shotResult.hit.playerId || null;
          hitData.isPlayer = shotResult.hit.isPlayer || false;
          
          shootingData.hit = hitData;
          
          // Log what we're sending to the server
          console.log('Sending hit data to server:', {
            bodyPart: hitData.bodyPart,
            damage: {
              base: weapon.damage,
              multiplied: shotResult.damage,
              multiplier: hitData.damageMultiplier
            },
            player: hitData.playerId
          });
        }
        
        // Send to server with callback to confirm receipt
        this.socket.emit('player-shoot', shootingData, (acknowledgement) => {
          if (acknowledgement) {
            console.log('Server acknowledged shot data');
          }
        });
      }
      
      // Dispatch a custom event for the UI to handle spawn protection
      const shootEvent = new CustomEvent('player-shoot', {
        detail: {
          weaponType: this.currentWeapon,
          isLocalPlayer: true,
          hit: shotResult,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(shootEvent);
    } else {
      console.error('Cannot send shot to server: Socket is null or undefined');
    }
    
    return true;
  }
  
  // Start automatic fire for machine gun
  startAutoFire() {
    const weapon = WEAPONS[this.currentWeapon];
    
    // Only allow auto-fire for automatic weapons
    if (!weapon.automatic) return;
    
    // Set auto-firing flag
    this.soundSystem.shootingMode.isAutoFiring = true;
    
    // Calculate the interval between shots based on fire rate
    const fireInterval = 1000 / weapon.fireRate;
    
    // Create an interval to simulate automatic fire
    this.soundSystem.shootingMode.autoFireInterval = setInterval(() => {
      // Continue firing as long as we can shoot and are still auto-firing
      if (this.canShoot() && this.soundSystem.shootingMode.isAutoFiring) {
        // Call shoot without setting auto-firing again to avoid recursion
        const originalAutoFiring = this.soundSystem.shootingMode.isAutoFiring;
        this.soundSystem.shootingMode.isAutoFiring = false;
        this.shoot();
        this.soundSystem.shootingMode.isAutoFiring = originalAutoFiring;
      } else {
        // Stop auto-firing if we can't shoot anymore
        this.stopAutoFire();
      }
    }, fireInterval);
    
    console.log(`Started automatic fire with interval: ${fireInterval}ms`);
  }
  
  // Stop automatic fire
  stopAutoFire() {
    if (this.soundSystem.shootingMode.autoFireInterval) {
      clearInterval(this.soundSystem.shootingMode.autoFireInterval);
      this.soundSystem.shootingMode.autoFireInterval = null;
      this.soundSystem.shootingMode.isAutoFiring = false;
      console.log('Stopped automatic fire');
    }
  }
  
  // Reload the current weapon
  reload() {
    const weapon = WEAPONS[this.currentWeapon];
    
    // Can't reload melee weapons
    if (weapon.isMelee) {
      return false;
    }
    
    // Already reloading
    if (this.state.isReloading) {
      return false;
    }
    
    // Don't need to reload if ammo is full
    if (this.ammo[this.currentWeapon] >= weapon.ammo) {
      return false;
    }
    
    // Start reloading
    this.state.isReloading = true;
    console.log(`Reloading ${weapon.name}...`);
    
    // Play reload animation
    this.playWeaponAnimation('reload', {
      loop: false,
      timeScale: 1.0,
      resetToIdle: true
    });
    
    // Play reload sound
    this.playSound(`${this.currentWeapon}_reload`, {
      volume: 0.7
    });
    
    // Schedule reload completion
    setTimeout(() => {
      // Set ammo to full magazine
      this.ammo[this.currentWeapon] = weapon.ammo;
      this.state.isReloading = false;
      console.log(`Reloaded ${weapon.name}. Ammo: ${this.ammo[this.currentWeapon]}`);
      
      // Call onWeaponReload callback if defined
      if (typeof this.onWeaponReload === 'function') {
        this.onWeaponReload(this.currentWeapon, this.ammo[this.currentWeapon]);
      }
    }, weapon.reloadTime * 1000);
    
    return true;
  }
  
  // Method to play weapon-specific animations
  playWeaponAnimation(animationName, options = {}) {
    if (!this.handsMixer) return;
    
    // Default options
    const defaultOptions = {
      loop: true,
      crossFadeDuration: 0.2,
      timeScale: 1.0,
      clampWhenFinished: false,
      resetToIdle: false
    };
    
    // Merge with provided options
    const finalOptions = { ...defaultOptions, ...options };
    
    // Try to find weapon-specific animation first
    let animation = null;
    
    // Check if we have specific animations for the current weapon
    if (this.handsAnimations[this.currentWeapon] && 
        this.handsAnimations[this.currentWeapon][animationName]) {
      animation = this.handsAnimations[this.currentWeapon][animationName];
      console.log(`Using ${this.currentWeapon}-specific ${animationName} animation`);
    } 
    // Fall back to global animations if weapon-specific ones aren't available
    else if (this.handsAnimations.global && this.handsAnimations.global[animationName]) {
      animation = this.handsAnimations.global[animationName];
      console.log(`Falling back to global ${animationName} animation`);
    }
    // Try finding any animation with a similar name
    else {
      // Search through all weapon types for similar animation names
      for (const weaponType in this.handsAnimations) {
        for (const name in this.handsAnimations[weaponType]) {
          if (name.includes(animationName) || animationName.includes(name)) {
            animation = this.handsAnimations[weaponType][name];
            console.log(`Found similar animation: ${name} from ${weaponType}`);
            break;
          }
        }
        if (animation) break;
      }
    }
    
    if (!animation) {
      console.log(`No animation found for: ${animationName} on weapon: ${this.currentWeapon}`);
      return;
    }
    
    // Stop all current animations with crossfade
    if (finalOptions.crossFadeDuration > 0) {
      this.handsMixer.stopAllAction();
    } else {
      this.handsMixer._actions.forEach(action => {
        action.stop();
      });
    }
    
    // Reset and configure the animation
    animation.reset();
    animation.setEffectiveTimeScale(finalOptions.timeScale);
    animation.setEffectiveWeight(1.0);
    animation.clampWhenFinished = finalOptions.clampWhenFinished;
    
    // Set loop mode
    animation.loop = finalOptions.loop ? THREE.LoopRepeat : THREE.LoopOnce;
    
    // Play the animation
    animation.play();
    
    // For non-looping animations, set up the callback to return to idle
    if (!finalOptions.loop && finalOptions.resetToIdle) {
      const mixer = this.handsMixer;
      const onFinished = function(e) {
        // Only proceed if this is our animation and it finished
        if (e.action === animation) {
          mixer.removeEventListener('finished', onFinished);
          // Small delay before going back to idle to make it look smoother
          setTimeout(() => {
            this.playWeaponAnimation('idle');
          }, 100);
        }
      }.bind(this);
      
      // Listen for the animation completion
      this.handsMixer.addEventListener('finished', onFinished);
    }
    
    console.log(`Playing ${animationName} animation for ${this.currentWeapon}`);
  }
  
  // Animation for melee attack
  animateMeleeAttack() {
    // Play the knife attack animation
    this.playWeaponAnimation('attack', {
      loop: false,
      timeScale: 1.5,
      resetToIdle: true
    });
    
    // The weapon models will move with the hands, no need to animate them separately
    // as they're attached to the hands bones that will be animated by the hands animation
  }
  
  // Animation for gun shot
  animateGunShot() {
    // Play the appropriate firing animation based on weapon type
    if (this.currentWeapon === WEAPON_TYPES.PISTOL) {
      this.playWeaponAnimation('shoot', {
        loop: false, 
        timeScale: 1.2,
        resetToIdle: true
      });
      
      // Add subtle pistol recoil effect
      if (this.handsModel) {
        // Instead of blocking updates, we'll use relative recoil
        // Store the original rotation values instead of the whole quaternion/vector
        if (!this.handsModel.userData.recoilOriginals) {
          this.handsModel.userData.recoilOriginals = {
            position: new THREE.Vector3(),
            rotation: new THREE.Euler()
          };
        }
        
        // Store current values before applying recoil
        this.handsModel.userData.recoilOriginals.position.copy(this.handsModel.position);
        this.handsModel.userData.recoilOriginals.rotation.copy(this.handsModel.rotation);
        
        // Create recoil offset object if it doesn't exist
        if (!this.handsModel.userData.recoilOffset) {
          this.handsModel.userData.recoilOffset = {
            position: new THREE.Vector3(0, 0, 0),
            rotation: new THREE.Vector3(0, 0, 0)
          };
        }
        
        // Set the recoil offset values - these will be added to the normal position
        // Use smaller values for the offset
        this.handsModel.userData.recoilOffset.position.set(0, 0, -0.1);    // Subtle movement back
        this.handsModel.userData.recoilOffset.rotation.set(0.05, 0, 0);  // Subtle upward rotation
        
        // Apply recoil immediately
        this.handsModel.position.add(this.handsModel.userData.recoilOffset.position);
        this.handsModel.rotation.x += this.handsModel.userData.recoilOffset.rotation.x;
        this.handsModel.rotation.y += this.handsModel.userData.recoilOffset.rotation.y;
        this.handsModel.rotation.z += this.handsModel.userData.recoilOffset.rotation.z;
        
        // Return to original position/rotation after short duration
        setTimeout(() => {
          const recoverRecoil = () => {
            // Gradually reduce the recoil offset
            this.handsModel.userData.recoilOffset.position.multiplyScalar(0.8);
            this.handsModel.userData.recoilOffset.rotation.multiplyScalar(0.8);
            
            // If recoil is almost done, clear it completely
            if (this.handsModel.userData.recoilOffset.position.length() < 0.001) {
              this.handsModel.userData.recoilOffset.position.set(0, 0, 0);
              this.handsModel.userData.recoilOffset.rotation.set(0, 0, 0);
              return; // Stop recovery
            }
            
            // Continue recovery
            requestAnimationFrame(recoverRecoil);
          };
          
          requestAnimationFrame(recoverRecoil);
        }, 50); // Short delay before recovery starts
      }
    } 
    else if (this.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
      this.playWeaponAnimation('shoot', {
        loop: false,
        timeScale: 1.0,
        resetToIdle: true
      });
      
      // Add subtle rifle recoil effect
      if (this.handsModel) {
        // Initialize recoil data structures if needed
        if (!this.handsModel.userData.recoilOriginals) {
          this.handsModel.userData.recoilOriginals = {
            position: new THREE.Vector3(),
            rotation: new THREE.Euler()
          };
        }
        
        // Store current values before applying recoil
        this.handsModel.userData.recoilOriginals.position.copy(this.handsModel.position);
        this.handsModel.userData.recoilOriginals.rotation.copy(this.handsModel.rotation);
        
        // Create recoil offset object if it doesn't exist
        if (!this.handsModel.userData.recoilOffset) {
          this.handsModel.userData.recoilOffset = {
            position: new THREE.Vector3(0, 0, 0),
            rotation: new THREE.Vector3(0, 0, 0)
          };
        }
        
        // Set the recoil offset values - slightly stronger for rifle
        this.handsModel.userData.recoilOffset.position.set(0, 0, -0.15);  
        
        // Add slight random sideways movement for rifle realism
        const sideRecoil = (Math.random() - 0.5) * 0.02;
        this.handsModel.userData.recoilOffset.rotation.set(0.08, sideRecoil, 0);
        
        // Apply recoil immediately
        this.handsModel.position.add(this.handsModel.userData.recoilOffset.position);
        this.handsModel.rotation.x += this.handsModel.userData.recoilOffset.rotation.x;
        this.handsModel.rotation.y += this.handsModel.userData.recoilOffset.rotation.y;
        this.handsModel.rotation.z += this.handsModel.userData.recoilOffset.rotation.z;
        
        setTimeout(() => {
          const recoverRecoil = () => {
            // Rifle has slower recovery (0.85 vs 0.8)
            this.handsModel.userData.recoilOffset.position.multiplyScalar(0.85);
            this.handsModel.userData.recoilOffset.rotation.multiplyScalar(0.85);
            
            // If recoil is almost done, clear it completely
            if (this.handsModel.userData.recoilOffset.position.length() < 0.001) {
              this.handsModel.userData.recoilOffset.position.set(0, 0, 0);
              this.handsModel.userData.recoilOffset.rotation.set(0, 0, 0);
              return; // Stop recovery
            }
            
            // Continue recovery
            requestAnimationFrame(recoverRecoil);
          };
          
          requestAnimationFrame(recoverRecoil);
        }, 60); // Slightly longer delay for rifle
      }
    }
  }
  
  // Update method to be called every frame
  update(camera) {
    // Update recoil recovery (add this first)
    this.updateRecoil();
    
    // Update target cache if enabled - do this early in the frame
    if (this.targetCache && this.targetCache.enabled) {
      this.updateTargetCache();
    }
    
    // Handle death animation for both hands and weapons if player is dead
    if (this.isPlayerDead && this.isDeathAnimationPlaying) {
      // Calculate death animation progress (over 1.2 seconds)
      const elapsed = Date.now() - this.deathAnimationStartTime;
      const duration = 1200; // 1.2 seconds
      const progress = Math.min(1, elapsed / duration);
      
      // Apply death animation to both hands and weapon models
      
      // First update the weapon models
      Object.values(this.weaponModels).forEach(model => {
        if (model.visible) {
          // Make the weapon follow the camera
          model.position.copy(camera.position);
          model.quaternion.copy(camera.quaternion);
          
          // Apply weapon-specific offsets in local space
          const config = WEAPONS[this.currentWeapon].model;
          model.translateX(config.position.x);
          model.translateY(config.position.y);
          model.translateZ(config.position.z);
          
          // Apply the death animation transformation
          if (progress < 1) {
            // Drop the weapon down and rotate forward
            model.rotation.x += Math.PI * 0.5 * progress; // Rotate down
            model.position.y -= 1.5 * progress; // Move down
          }
        }
      });
      
      // Then update the hands model
      if (this.handsModel) {
        // Make hands follow camera
        this.handsModel.position.copy(camera.position);
        this.handsModel.quaternion.copy(camera.quaternion);
        
        // Animate hands falling down below the screen
        if (progress < 1) {
          // Drop the hands down and rotate forward as if falling
          this.handsModel.rotation.x += Math.PI * 0.5 * progress; // Rotate down
          this.handsModel.position.y -= 1.5 * progress; // Move down
        }
        
        // Update any attached weapons to follow hands
        if (this.attachedWeaponUpdateFunctions) {
          this.attachedWeaponUpdateFunctions.forEach(updateFn => updateFn());
        }
      }
      
      // Update hands animations if mixer exists
      if (this.handsMixer) {
        this.handsMixer.update(0.016); // Update with a fixed delta time
      }
      
      return; // Skip regular updates
    }
    
    // Normal update logic when not in death animation
    // Update weapon positions to follow camera
    Object.values(this.weaponModels).forEach(model => {
      if (model.visible) {
        // Make the weapon follow the camera
        model.position.copy(camera.position);
        model.quaternion.copy(camera.quaternion);
        
        // Apply weapon-specific offsets in local space
        const config = WEAPONS[this.currentWeapon].model;
        
        // Apply position offset in local space
        model.translateX(config.position.x);
        model.translateY(config.position.y);
        model.translateZ(config.position.z);
      }
    });
    
    // Position hands model the same way as weapons
    if (this.handsModel) {
      // Get weapon config for current weapon to use the same offsets
      const weaponConfig = WEAPONS[this.currentWeapon].model;
      
      // Make hands follow camera
      this.handsModel.position.copy(camera.position);
      this.handsModel.quaternion.copy(camera.quaternion);
      
      // Base position offsets
      let xOffset = -0.2;
      let yOffset = -3.6;
      let zOffset = 1.4;
      
      // Weapon-specific adjustments
      if (this.currentWeapon === WEAPON_TYPES.KNIFE) {
        xOffset = -0.02;    // Horizontal position 
        yOffset = -3.5;    // Vertical position - less negative to raise hands up
        zOffset = 1.5;     // Distance from camera
        
        // Add rotation for knife hands if needed
        this.handsModel.rotateX(Math.PI * 0.15);  // Tilt hands up/down
        this.handsModel.rotateY(Math.PI * 0.2);   // Angle hands left/right
        this.handsModel.rotateZ(Math.PI * 0.0);   // Roll hands
      }
      else if (this.currentWeapon === WEAPON_TYPES.PISTOL) {
        xOffset = 0.35;   // Horizontal position
        yOffset = -3.8;   // Vertical position
        zOffset = 1.0;    // Distance from camera
        
        // Add rotation for pistol hands
        // Apply these rotations AFTER the quaternion.copy but BEFORE the translates
        this.handsModel.rotateX(Math.PI * 0.014);  // Tilt hands up/down
        this.handsModel.rotateY(Math.PI * 0.17); // Angle hands left/right
        this.handsModel.rotateZ(Math.PI * 0.00); // Roll hands
      } 
      else if (this.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
        xOffset = -0.4;
        yOffset = -2.25;
        zOffset = 0.7;
      }
      
      // Apply base weapon offsets plus our custom adjustments
      this.handsModel.translateX(weaponConfig.position.x + xOffset);
      this.handsModel.translateY(weaponConfig.position.y + yOffset); 
      this.handsModel.translateZ(weaponConfig.position.z + zOffset);
      
      // Apply recoil offset after normal positioning if it exists
      if (this.handsModel.userData.recoilOffset) {
        // Add the current recoil offset to the current position
        this.handsModel.position.add(this.handsModel.userData.recoilOffset.position);
        this.handsModel.rotation.x += this.handsModel.userData.recoilOffset.rotation.x;
        this.handsModel.rotation.y += this.handsModel.userData.recoilOffset.rotation.y;
        this.handsModel.rotation.z += this.handsModel.userData.recoilOffset.rotation.z;
      }
      
      // Ensure the hands model is visible when a weapon is visible
      const currentWeaponModel = this.weaponModels[this.currentWeapon];
      if (currentWeaponModel) {
        this.handsModel.visible = currentWeaponModel.visible;
      }
      
      // Update any attached weapons to follow hands
      if (this.attachedWeaponUpdateFunctions) {
        this.attachedWeaponUpdateFunctions.forEach(updateFn => updateFn());
      }
    }
    
    // Update hands animations if mixer exists
    if (this.handsMixer) {
      this.handsMixer.update(0.016); // Update with a fixed delta time
    }
  }
  
  // Clean up resources
  dispose() {
    // Stop any ongoing auto-fire
    this.stopAutoFire();
    
    // Clean up sound resources
    if (this.soundSystem && this.soundSystem.sounds) {
      Object.values(this.soundSystem.sounds).forEach(sound => {
        sound.pause();
        sound.src = '';
      });
      this.soundSystem.sounds = {};
      
      // Close audio context if it exists
      if (this.soundSystem.audioContext) {
        if (this.soundSystem.audioContext.state !== 'closed') {
          try {
            this.soundSystem.audioContext.close();
          } catch (e) {
            console.warn('Error closing audio context:', e);
          }
        }
        this.soundSystem.audioContext = null;
      }
    }
    
    // Remove all weapon models from scene
    Object.values(this.weaponModels).forEach(model => {
      if (model.parent) {
        model.parent.remove(model);
      }
      
      // Dispose of geometries and materials
      model.traverse(child => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    });
    
    // Clean up hands model and any attached weapons
    if (this.handsModel) {
      // First, clean up any attached weapon models
      if (this.handsModel.userData && this.handsModel.userData.attachedWeapon) {
        const attachedWeapon = this.handsModel.userData.attachedWeapon;
        if (attachedWeapon.parent) {
          attachedWeapon.parent.remove(attachedWeapon);
        }
        
        // Dispose of attached weapon geometries and materials
        attachedWeapon.traverse(child => {
          if (child instanceof THREE.Mesh) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => material.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
        
        this.handsModel.userData.attachedWeapon = null;
      }
      
      // Now remove the hands model itself
      if (this.handsModel.parent) {
        this.handsModel.parent.remove(this.handsModel);
      }
      
      // Dispose of hands geometries and materials
      this.handsModel.traverse(child => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    }
    
    // Clear the animation mixer
    if (this.handsMixer) {
      this.handsMixer.stopAllAction();
      this.handsAnimations = {};
    }
    
    // Clear references
    this.weaponModels = {};
    this.handsModel = null;
    this.attachedWeaponUpdateFunctions = [];
  }
  
  // Create a ray from camera for hit detection
  createRay() {
    // Create a ray from the camera's position pointing forward
    const raycaster = new THREE.Raycaster();
    
    // Set raycaster origin to camera position
    raycaster.ray.origin.copy(this.camera.position);
    
    // Set direction to where the camera is pointing (forward vector)
    const direction = new THREE.Vector3(0, 0, -1);
    direction.applyQuaternion(this.camera.quaternion);
    
    // Apply recoil if any
    if (this.recoilState.current > 0) {
      // Calculate recoil spread angle based on current recoil value
      const recoilSpread = this.recoilState.current;
      
      // Add a random offset within the recoil cone
      // Stronger vertical recoil (shooting high) than horizontal
      const randomAngleX = (Math.random() * recoilSpread) - (recoilSpread * 0.2); // Mostly up
      const randomAngleY = (Math.random() * recoilSpread * 0.5) - (recoilSpread * 0.25); // Evenly left/right
      
      // Create temporary rotation object
      const recoilRotation = new THREE.Euler(
        -randomAngleX, // Negative to shoot higher (rotates up)
        randomAngleY,  // Random horizontal deviation
        0
      );
      
      // Apply the rotation to the direction vector
      direction.applyEuler(recoilRotation);
    }
    
    // Set the final direction with recoil applied
    raycaster.ray.direction.copy(direction);
    
    return raycaster;
  }
  
  // Handle melee attack
  handleMeleeAttack(raycaster, weapon) {
    // Calculate hit with short range
    const hit = this.calculateHit(raycaster, weapon.range);
    
    if (hit) {
      // Calculate damage with body part multiplier if it's a player
      let damage = weapon.damage;
      if (hit.isPlayer && hit.damageMultiplier) {
        damage = Math.round(weapon.damage * hit.damageMultiplier);
        console.log(`Melee hit with ${weapon.name} on ${hit.bodyPart}! Base damage: ${weapon.damage}, multiplier: ${hit.damageMultiplier}x, total: ${damage}`);
        
        // Play knife hit sound if it's a player
        this.playSound(`${WEAPON_TYPES.KNIFE}_hit`, {
          volume: 0.8,
          randomizePitch: true
        });
      } else {
        console.log(`Melee hit with ${weapon.name}! Distance: ${hit.distance.toFixed(2)}, Damage: ${damage}`);
        
        // Play a different sound for hitting an object
        this.playSound('hit_object', {
          volume: 0.6,
          randomizePitch: true
        });
      }
      
      // Create visual effect at the hit point
      this.createHitEffect(hit.position, hit.bodyPart);
      
      // Return hit result with damage information
      return {
        hit: hit,
        damage: damage,
        bodyPart: hit.bodyPart || 'default'
      };
    } else {
      console.log(`Melee attack with ${weapon.name} - no hit within range`);
      return null;
    }
  }
  
  // Handle gun shot (pistol or machine gun)
  handleGunShot(raycaster, weapon) {
    // Calculate hit with weapon range
    const hit = this.calculateHit(raycaster, weapon.range);
    
    if (hit) {
      // Calculate damage with body part multiplier if it's a player
      let damage = weapon.damage;
      if (hit.isPlayer && hit.damageMultiplier) {
        damage = Math.round(weapon.damage * hit.damageMultiplier);
        console.log(`Shot hit with ${weapon.name} on ${hit.bodyPart}! Base damage: ${weapon.damage}, multiplier: ${hit.damageMultiplier}x, total: ${damage}`);
      } else {
        console.log(`Hit with ${weapon.name}! Distance: ${hit.distance.toFixed(2)}, Damage: ${damage}`);
      }
      
      // Create visual effect at the hit point
      this.createHitEffect(hit.position, hit.bodyPart);
      
      // Return hit result with damage information
      return {
        hit: hit,
        damage: damage,
        bodyPart: hit.bodyPart || 'default'
      };
    } else {
      console.log(`Shot with ${weapon.name} - missed or out of range`);
      return null;
    }
  }
  
  // Calculate if a shot hits anything
  calculateHit(raycaster, maxRange) {
    // Start performance measurement
    const startTime = performance.now();
    let traverseTime = 0;
    let intersectTime = 0;
    let processTime = 0;

    // Start traverse timing
    const traverseStartTime = performance.now();
    
    // Get targets - either from cache or fresh from scene
    let targets = [];
    let hitboxTargets = []; // Specialized array just for hitboxes
    
    if (this.targetCache.enabled) {
      // Use cached targets if available and fresh enough
      const cachedTargets = this.updateTargetCache();
      
      // Check if we got hitbox targets from the cache
      if (this.targetCache.hitboxTargets && this.targetCache.hitboxTargets.length > 0) {
        hitboxTargets = this.targetCache.hitboxTargets;
        targets = this.targetCache.targets || []; // Get regular targets too as fallback
      } else {
        targets = cachedTargets;
      }
    } else {
      // Count total objects in scene for statistics
      let totalObjectCount = 0;
      this.scene.traverse(() => { totalObjectCount++; });
      this.hitStats.objectsInScene = totalObjectCount;
      
      // Get fresh targets from scene
      targets = [];
      hitboxTargets = []; // Clear hitbox targets
      let localPlayerFound = false;
      
      this.scene.traverse((object) => {
        // Skip the local player's model to avoid hitting ourselves
        if (object.userData && object.userData.playerId === this.localPlayerId) {
          localPlayerFound = true;
          return;
        }
        
        // Check if this is a hitbox mesh (preferred for hit detection)
        if (object.isMesh && object.name.startsWith('Hitbox')) {
          // Add to hitbox targets collection 
          hitboxTargets.push(object);
          console.log(`[Hitbox Target Added]: ${object.name} (Player: ${object.userData?.playerId})`); // DEBUG LOG
          
          // Make sure it's ray-castable even if invisible
          if (!object.visible) {
            // We need the mesh to be visible to the raycaster but not to the camera
            // Store original visibility to restore it later
            object.userData._wasVisible = object.visible;
            
            // Special case: make it visible but only for raycasting
            // This won't make it render visually
            object.visible = true;
            if (object.material) {
              object.material.visible = false;
            }
          }
        } 
        // Also include regular objects for fallback
        else if (object.userData && (object.userData.isPlayer || object.userData.isObstacle)) {
          targets.push(object);
          console.log(`[Regular Target Added]: ${object.name} (Player: ${object.userData?.playerId}, isPlayer: ${object.userData?.isPlayer})`); // DEBUG LOG
        }
      });
      
      console.log(`Found ${hitboxTargets.length} hitbox meshes for hit detection`);
      
      // Store potential targets count for statistics 
      this.hitStats.potentialTargets = targets.length + hitboxTargets.length;
    }
    
    // End traverse timing
    traverseTime = performance.now() - traverseStartTime;
    
    // Log hit detection status
    console.log(`Hit detection with ${hitboxTargets.length} hitbox targets and ${targets.length} regular targets.`);
    
    // Start intersect timing
    const intersectStartTime = performance.now();
    
    // First try with just hitbox meshes (more accurate)
    let intersects = [];
    if (hitboxTargets.length > 0) {
      intersects = raycaster.intersectObjects(hitboxTargets, false); // No need for recursive here
      console.log(`[Hitbox Intersect Results]: Count=${intersects.length}, HitMeshes=[${intersects.map(i => i.object.name).join(', ')}]`); // DEBUG LOG
    }
    
    // If no hitbox hits, fall back to regular meshes
    if (intersects.length === 0) {
      intersects = raycaster.intersectObjects(targets, true);
      console.log(`[Regular Intersect Results]: Count=${intersects.length}, HitMeshes=[${intersects.map(i => i.object.name).join(', ')}]`); // DEBUG LOG
    }
    
    // End intersect timing
    intersectTime = performance.now() - intersectStartTime;
    
    // Clean up - restore visibility states
    hitboxTargets.forEach(hitbox => {
      if (hitbox.userData._wasVisible !== undefined) {
        hitbox.visible = hitbox.userData._wasVisible;
        delete hitbox.userData._wasVisible;
      }
    });
    
    // Start process timing
    const processStartTime = performance.now();
    
    // Increment total shots counter
    this.hitStats.totalShots++;
    
    if (intersects.length > 0) {
      // Get the first hit (closest to the camera)
      const hit = intersects[0];
      
      // Check if the hit is within the weapon's range
      if (hit.distance <= maxRange) {
        // The specific mesh that was hit
        const hitMesh = hit.object;
        
        // Default body part and multiplier
        let bodyPart = 'default';
        let damageMultiplier = this.getBodyPartMultiplier(bodyPart);
        
        // Determine the body part based on mesh name or position
        if (hitMesh.isMesh) {
          const meshName = hitMesh.name.toLowerCase();
          
          // Try to identify body part from mesh name
          bodyPart = this.identifyBodyPartFromMesh(hitMesh, meshName);
          damageMultiplier = this.getBodyPartMultiplier(bodyPart);
          
          console.log(`Hit mesh: ${meshName}, identified as ${bodyPart}, multiplier: ${damageMultiplier}x`);
        }
        
        // Trace up the parent chain until we find the player or object group
        let targetObject = hitMesh;
        let isPlayer = false;
        let playerId = null;
        
        // Traverse up the parent chain until we find the root player object or scene
        while (targetObject && targetObject !== this.scene) {
          if (targetObject.userData && targetObject.userData.playerId) {
            isPlayer = targetObject.userData.isPlayer;
            playerId = targetObject.userData.playerId;
            
            // Double-check we're not hitting local player (shouldn't happen but just in case)
            if (playerId === this.localPlayerId) {
              console.log(`Secondary check caught local player hit, ignoring`);
              processTime = performance.now() - processStartTime;
              const totalTime = performance.now() - startTime;
              
              // Update timing statistics
              this.updateTimingStats(totalTime, traverseTime, intersectTime, processTime);
              
              // Update miss statistics
              this.hitStats.misses++;
              
              console.log(`Hit detection performance: Total=${totalTime.toFixed(2)}ms (Traverse=${traverseTime.toFixed(2)}ms, Intersect=${intersectTime.toFixed(2)}ms, Process=${processTime.toFixed(2)}ms)`);
              return null;
            }
            
            // Check if the player is already dead - if so, ignore the hit
            if (isPlayer && targetObject.userData.isDead === true) {
              console.log(`Ignoring hit on already dead player: ${playerId}`);
              processTime = performance.now() - processStartTime;
              const totalTime = performance.now() - startTime;
              
              // Update timing statistics
              this.updateTimingStats(totalTime, traverseTime, intersectTime, processTime);
              
              // Update miss statistics
              this.hitStats.misses++;
              
              console.log(`Hit detection performance: Total=${totalTime.toFixed(2)}ms (Traverse=${traverseTime.toFixed(2)}ms, Intersect=${intersectTime.toFixed(2)}ms, Process=${processTime.toFixed(2)}ms)`);
              return null;
            }
            
            break;
          }
          targetObject = targetObject.parent;
        }
        
        // Log the successful hit
        console.log(`Hit detected on ${isPlayer ? 'player' : 'object'} ${playerId || 'unknown'} at distance ${hit.distance.toFixed(2)}`);
        if (isPlayer) {
          console.log(`Body part: ${bodyPart} with damage multiplier ${damageMultiplier}x`);
        }
        
        // End process timing
        processTime = performance.now() - processStartTime;
        
        // Calculate total time
        const totalTime = performance.now() - startTime;
        
        // Update timing statistics
        this.updateTimingStats(totalTime, traverseTime, intersectTime, processTime);
        
        // Update hit statistics
        this.hitStats.hits++;
        this.hitStats.hitsByBodyPart[bodyPart]++;
        this.hitStats.hitsByWeapon[this.currentWeapon]++;
        
        // Log performance metrics
        console.log(`Hit detection performance: Total=${totalTime.toFixed(2)}ms (Traverse=${traverseTime.toFixed(2)}ms, Intersect=${intersectTime.toFixed(2)}ms, Process=${processTime.toFixed(2)}ms)`);
        
        // Return hit information with body part and damage multiplier
        return {
          position: hit.point,
          distance: hit.distance,
          object: targetObject,
          hitMesh: hitMesh,  // Add the specific mesh that was hit
          bodyPart: bodyPart,
          damageMultiplier: damageMultiplier,
          isPlayer: isPlayer,
          playerId: playerId
        };
      }
    }
    
    // End process timing
    processTime = performance.now() - processStartTime;
    
    // Calculate total time
    const totalTime = performance.now() - startTime;
    
    // Update timing statistics
    this.updateTimingStats(totalTime, traverseTime, intersectTime, processTime);
    
    // Update miss statistics
    this.hitStats.misses++;
    
    // Log performance metrics for misses too
    console.log(`Hit detection performance (no hit): Total=${totalTime.toFixed(2)}ms (Traverse=${traverseTime.toFixed(2)}ms, Intersect=${intersectTime.toFixed(2)}ms, Process=${processTime.toFixed(2)}ms)`);
    
    // No hit or hit is out of range
    return null;
  }
  
  // Extract body part identification to a separate method
  identifyBodyPartFromMesh(hitMesh, meshName) {
    // First, check if this is a specialized hitbox mesh
    if (meshName.startsWith('hitbox_')) {
      // Direct mapping from hitbox mesh name to body part
      if (meshName.includes('hitbox_head')) {
        return 'head';
      } else if (meshName.includes('hitbox_torso')) {
        return 'torso';
      } else if (meshName.includes('hitbox_limb')) {
        return 'limb';
      } else {
        console.log(`Found hitbox but couldn't identify specific part: ${meshName}`);
        return 'default';
      }
    }
    
    // Fall back to existing name-based detection for regular meshes
    if (meshName.includes('head') || meshName.includes('helmet') || 
        meshName.includes('face') || meshName.includes('hair') || 
        meshName.includes('skull')) {
      return 'head';
    } else if (meshName.includes('torso') || meshName.includes('chest') || 
              meshName.includes('spine') || meshName.includes('hip') || 
              meshName.includes('outfit_top') || meshName.includes('body') ||
              meshName.includes('top') || meshName.includes('shirt')) {
      return 'torso';
    } else if (meshName.includes('arm') || meshName.includes('leg') || 
              meshName.includes('thigh') || meshName.includes('calf') ||
              meshName.includes('knee') || meshName.includes('elbow') || 
              meshName.includes('outfit_bottom') || meshName.includes('pants') ||
              meshName.includes('bottom') || meshName.includes('shorts')) {
      return 'limb';
    } else if (meshName.includes('hand') || meshName.includes('foot') || 
              meshName.includes('finger') || meshName.includes('toe') ||
              meshName.includes('shoe') || meshName.includes('glove')) {
      return 'hand';
    } else {
      // If we can't determine from the name, use the relative height
      
      // Log unrecognized mesh name to help debug
      console.log(`Unrecognized mesh name: ${meshName}, using positional detection...`);
      
      // Trace up to find the player root
      let playerRoot = hitMesh;
      while (playerRoot && playerRoot !== this.scene) {
        if (playerRoot.userData && playerRoot.userData.isPlayer) {
          break;
        }
        playerRoot = playerRoot.parent;
      }
      
      if (playerRoot && playerRoot.userData && playerRoot.userData.isPlayer) {
        // Calculate relative height of hit within the player model
        const playerHeight = playerRoot.userData.height || 1.7; // Fallback height
        const rootY = playerRoot.position.y;
        const hitRelativeY = hitMesh.position.y - rootY;
        const relativeHeightRatio = hitRelativeY / playerHeight;
        
        console.log(`Hit at relative height ratio: ${relativeHeightRatio.toFixed(2)}`);
        
        // Classify based on relative height with adjusted thresholds
        if (relativeHeightRatio > 0.85) {
          return 'head';
        } else if (relativeHeightRatio > 0.65) { // Lowered from 0.5 to 0.65
          return 'torso';
        } else if (relativeHeightRatio > 0.1) {
          return 'limb';
        } else {
          return 'hand';
        }
      }
      
      return 'default';
    }
  }
  
  // Extract body part multipliers to a separate method
  getBodyPartMultiplier(bodyPart) {
    // Body part damage multipliers
    const bodyPartMultipliers = {
      head: 2.5,     // Headshots do 2.5x damage
      torso: 1.0,    // Torso is baseline damage
      limb: 0.75,    // Arms and legs do 0.75x damage
      hand: 0.5,     // Hands and feet do 0.5x damage
      default: 1.0   // Default multiplier if part can't be determined
    };
    
    return bodyPartMultipliers[bodyPart] || bodyPartMultipliers.default;
  }
  
  // Add target cache settings to the hit stats report
  getHitStatsReport() {
    const accuracy = this.hitStats.totalShots > 0 
      ? (this.hitStats.hits / this.hitStats.totalShots * 100).toFixed(1) 
      : '0.0';
    
    return {
      shots: {
        total: this.hitStats.totalShots,
        hits: this.hitStats.hits,
        misses: this.hitStats.misses,
        accuracy: `${accuracy}%`
      },
      bodyParts: {
        head: this.hitStats.hitsByBodyPart.head,
        torso: this.hitStats.hitsByBodyPart.torso,
        limb: this.hitStats.hitsByBodyPart.limb,
        hand: this.hitStats.hitsByBodyPart.hand,
        other: this.hitStats.hitsByBodyPart.default
      },
      weapons: {
        knife: this.hitStats.hitsByWeapon[WEAPON_TYPES.KNIFE],
        pistol: this.hitStats.hitsByWeapon[WEAPON_TYPES.PISTOL],
        machineGun: this.hitStats.hitsByWeapon[WEAPON_TYPES.MACHINE_GUN]
      },
      timing: {
        averageTotal: `${this.hitStats.averageTotalTime.toFixed(2)}ms`,
        averageTraverse: `${this.hitStats.averageTraverseTime.toFixed(2)}ms`,
        averageIntersect: `${this.hitStats.averageIntersectTime.toFixed(2)}ms`,
        averageProcess: `${this.hitStats.averageProcessTime.toFixed(2)}ms`
      },
      scene: {
        totalObjects: this.hitStats.objectsInScene,
        potentialTargets: this.hitStats.potentialTargets
      },
      targetCache: {
        enabled: this.targetCache.enabled,
        updateInterval: `${this.targetCache.updateInterval}ms`,
        cachedTargets: this.targetCache.cachedCount,
        timeSinceUpdate: `${(performance.now() - this.targetCache.lastUpdateTime).toFixed(0)}ms`,
        sceneVersion: this.targetCache.sceneVersion,
        hitsMissedDueToStaleCache: this.targetCache.hitsMissedDueToStaleCache
      }
    };
  }
  
  // Method to toggle target caching
  toggleTargetCaching(enabled = null) {
    if (enabled === null) {
      // Toggle if no specific value provided
      this.targetCache.enabled = !this.targetCache.enabled;
    } else {
      // Otherwise use the provided value
      this.targetCache.enabled = !!enabled;
    }
    
    // Force update cache if enabling
    if (this.targetCache.enabled) {
      this.updateTargetCache(true);
    }
    
    console.log(`Target caching ${this.targetCache.enabled ? 'enabled' : 'disabled'}`);
    return this.targetCache.enabled;
  }
  
  // Method to set target cache update interval
  setTargetCacheUpdateInterval(milliseconds) {
    if (milliseconds > 0) {
      this.targetCache.updateInterval = milliseconds;
      console.log(`Target cache update interval set to ${milliseconds}ms`);
      return true;
    }
    return false;
  }
  
  // Method to reset hit statistics
  resetHitStats() {
    this.hitStats = {
      totalShots: 0,
      hits: 0,
      misses: 0,
      hitsByBodyPart: {
        head: 0,
        torso: 0,
        limb: 0,
        hand: 0,
        default: 0
      },
      hitsByWeapon: {
        [WEAPON_TYPES.KNIFE]: 0,
        [WEAPON_TYPES.PISTOL]: 0,
        [WEAPON_TYPES.MACHINE_GUN]: 0
      },
      averageTotalTime: 0,
      averageTraverseTime: 0,
      averageIntersectTime: 0,
      averageProcessTime: 0,
      lastTotalTimes: [],
      lastTraverseTimes: [],
      lastIntersectTimes: [],
      lastProcessTimes: [],
      objectsInScene: 0,
      potentialTargets: 0
    };
    
    // Reset target cache stats but keep settings
    this.targetCache.hitsMissedDueToStaleCache = 0;
    this.targetCache.sceneVersion = 0;
    
    console.log('Hit statistics and target cache metrics reset');
  }
  
  // New method to update the target cache
  updateTargetCache(force = false) {
    const now = performance.now();
    
    // Skip update if cache is disabled
    if (!this.targetCache.enabled) {
      return [];
    }
    
    // Check if cache update is needed
    if (!force && now - this.targetCache.lastUpdateTime < this.targetCache.updateInterval) {
      // If we already have hitbox targets cached, return those
      if (this.targetCache.hitboxTargets && this.targetCache.hitboxTargets.length > 0) {
        return this.targetCache.hitboxTargets;
      }
      return this.targetCache.targets;
    }
    
    // console.log("Updating target cache...");
    
    // Get fresh targets from scene
    const targets = [];
    const hitboxTargets = []; // New array for hitbox meshes
    let localPlayerFound = false;
    let totalObjectCount = 0;
    
    // Count total objects while traversing
    this.scene.traverse((object) => {
      totalObjectCount++;
      
      // Skip the local player's model
      if (object.userData && object.userData.playerId === this.localPlayerId) {
        localPlayerFound = true;
        return;
      }
      
      // Check for hitbox meshes
      if (object.isMesh && object.name.startsWith('Hitbox')) {
        hitboxTargets.push(object);
        
        // Make sure it's ray-castable even if invisible
        if (!object.visible) {
          // We need the mesh to be visible to the raycaster but not to the camera
          // Store original visibility to restore it later
          object.userData._wasVisible = object.visible;
          
          // Special case: make it visible but only for raycasting
          // This won't make it render visually
          object.visible = true;
          if (object.material) {
            object.material.visible = false;
          }
        }
      }
      // Also collect regular objects as fallback
      else if (object.userData && (object.userData.isPlayer || object.userData.isObstacle)) {
        targets.push(object);
      }
    });
    
    // Update cache
    this.targetCache.targets = targets;
    this.targetCache.hitboxTargets = hitboxTargets; // Store hitbox targets separately
    this.targetCache.lastUpdateTime = now;
    this.targetCache.cachedCount = targets.length + hitboxTargets.length;
    this.targetCache.sceneVersion++;
    
    // Update scene stats
    this.hitStats.objectsInScene = totalObjectCount;
    this.hitStats.potentialTargets = this.targetCache.cachedCount;
    
    console.log(`Target cache updated with ${hitboxTargets.length} hitbox targets and ${targets.length} regular targets`);
    
    // Return hitbox targets if available, otherwise regular targets
    return hitboxTargets.length > 0 ? hitboxTargets : targets;
  }
  
  // Add a method to play hands animations
  playHandsAnimation(animationName) {
    if (!this.handsMixer || !this.handsAnimations) return;
    
    const animation = this.handsAnimations[animationName];
    if (!animation) {
      console.log(`No hands animation found for: ${animationName}`);
      return;
    }
    
    // Get current animation and fade out
    this.handsMixer.stopAllAction();
    
    // Play new animation
    animation.reset();
    animation.setEffectiveTimeScale(1.0);
    animation.setEffectiveWeight(1.0);
    animation.play();
    
    console.log(`Playing hands animation: ${animationName}`);
  }
  
  // Add a new method to adjust hands model for different weapons
  adjustHandsForWeapon(weaponType) {
    if (!this.handsModel || !this.handsModel.children[0]) return;
    
    const handsScene = this.handsModel.children[0];
    
    // Use the same scale for all weapons (pistol scale)
    handsScene.scale.set(2.5, 2.5, 2.5);
    
    // Remove any previously attached weapon models from hands
    if (this.handsModel.userData && this.handsModel.userData.attachedWeapon) {
      const attachedWeapon = this.handsModel.userData.attachedWeapon;
      if (attachedWeapon.parent) {
        attachedWeapon.parent.remove(attachedWeapon);
      }
      this.handsModel.userData.attachedWeapon = null;
    }
    
    // Clear attached weapon update functions
    if (this.attachedWeaponUpdateFunctions) {
      this.attachedWeaponUpdateFunctions = [];
    }
    
    // Get weapon configuration
    const weaponConfig = WEAPONS[weaponType];
    if (!weaponConfig) return;
    
    // Model paths for each weapon
    const modelPaths = {
      [WEAPON_TYPES.KNIFE]: '/models/knife.glb',
      [WEAPON_TYPES.PISTOL]: '/models/pistol.glb',
      [WEAPON_TYPES.MACHINE_GUN]: '/models/ak.glb'
    };
    
    // Only proceed if we have a model path for this weapon
    if (!modelPaths[weaponType]) {
      console.warn(`No model path defined for weapon type: ${weaponType}`);
      return;
    }
    
    // Load and attach the appropriate weapon model
    this.loadModel(modelPaths[weaponType])
      .then(weaponGltf => {
        const weaponContainer = markRaw(new THREE.Group());
        const weaponModel = weaponGltf.scene;
        
        // Add the weapon model to our container
        weaponContainer.add(weaponModel);
        
        // Apply weapon-specific scaling and adjustments
        this.applyWeaponModelAdjustments(weaponType, weaponModel, weaponContainer);
        
        // Make sure all meshes are visible with proper materials
        weaponModel.traverse(obj => {
          if (obj.isMesh) {
            obj.visible = true;
            if (obj.material) {
              if (Array.isArray(obj.material)) {
                obj.material.forEach(mat => {
                  mat.transparent = false;
                  mat.opacity = 1.0;
                  mat.alphaTest = 0.5;
                  mat.needsUpdate = true;
                });
              } else {
                obj.material.transparent = false;
                obj.material.opacity = 1.0;
                obj.material.alphaTest = 0.5;
                obj.material.needsUpdate = true;
              }
            }
          }
        });
        
        // Try to find right hand bone in the hands model
        let rightHandBone = null;
        handsScene.traverse(node => {
          if (node.isBone && node.name.toLowerCase().includes('right') && 
              node.name.toLowerCase().includes('hand')) {
            rightHandBone = node;
          }
        });
        
        if (rightHandBone) {
          // Add directly to the scene for matrix-based positioning
          this.scene.add(weaponContainer);
          
          // Store references for update function
          weaponContainer.userData.referenceBone = rightHandBone;
          weaponContainer.userData.referenceModel = this.handsModel;
          weaponContainer.userData.weaponType = weaponType;
          
          // Position the weapon initially
          weaponModel.position.set(0, 0, 0);
          
          // Setup update function to follow bone
          const updateWeaponPosition = () => {
            if (weaponContainer.userData.referenceBone && weaponContainer.userData.referenceModel) {
              const bone = weaponContainer.userData.referenceBone;
              const model = weaponContainer.userData.referenceModel;
              
              // Skip if model is no longer in scene
              if (!model.parent) {
                if (weaponContainer.parent) {
                  weaponContainer.parent.remove(weaponContainer);
                }
                return;
              }
              
              // Update matrices
              bone.updateWorldMatrix(true, false);
              model.updateWorldMatrix(true, false);
              
              // Get world position and quaternion of the bone
              const boneWorldPos = new THREE.Vector3();
              const boneWorldQuat = new THREE.Quaternion();
              const boneWorldScale = new THREE.Vector3();
              
              // Decompose the bone's world matrix
              bone.matrixWorld.decompose(boneWorldPos, boneWorldQuat, boneWorldScale);
              
              // Create a matrix for precise positioning
              const positionMatrix = new THREE.Matrix4();
              positionMatrix.makeRotationFromQuaternion(boneWorldQuat);
              
              // Set weapon container position and rotation to match bone exactly
              weaponContainer.position.copy(boneWorldPos);
              weaponContainer.quaternion.copy(boneWorldQuat);
              
              // Apply weapon-specific adjustments
              this.applyWeaponPositionAdjustments(weaponType, weaponContainer, weaponModel);
            }
          };
          
          // Initial position update
          updateWeaponPosition();
          
          // Store the update function
          weaponContainer.userData.updatePosition = updateWeaponPosition;
          
          // Add to update functions to be called in the main update loop
          if (!this.attachedWeaponUpdateFunctions) {
            this.attachedWeaponUpdateFunctions = [];
          }
          this.attachedWeaponUpdateFunctions.push(updateWeaponPosition);
          
        } else {
          // Fallback if hand bone not found
          this.handsModel.add(weaponContainer);
          
          // Apply fallback positioning based on weapon type
          if (weaponType === WEAPON_TYPES.KNIFE) {
            weaponContainer.position.set(0.1, 0.1, 0.2);
          } else if (weaponType === WEAPON_TYPES.PISTOL) {
            weaponContainer.position.set(0.0, 0.1, 0.3);
          } else {
            weaponContainer.position.set(0.0, 0.2, 0.3);
          }
          
          weaponModel.rotation.set(0, 0, 0);
        }
        
        // Store reference for later removal
        this.handsModel.userData.attachedWeapon = weaponContainer;
        
        // Play the appropriate idle animation for this weapon
        this.playWeaponAnimation('idle');
        
        console.log(`Attached ${weaponType} model to hands`);
      })
      .catch(error => {
        console.error(`Failed to attach ${weaponType} model to hands:`, error);
      });
    
    console.log(`Adjusted hands for weapon type: ${weaponType}`);
  }
  
  // New method to apply weapon-specific model adjustments
  applyWeaponModelAdjustments(weaponType, weaponModel, weaponContainer) {
    // Ensure weapon is exempt from frustum culling
    weaponContainer.userData.isWeaponHands = true;
    weaponContainer.userData.alwaysVisible = true;
    weaponContainer.frustumCulled = false;
    
    // Mark all children as weapon hands too
    weaponModel.traverse(child => {
      if (child.isMesh) {
        child.frustumCulled = false;
        child.userData.isWeaponHands = true;
        child.userData.alwaysVisible = true;
      }
    });
    
    switch(weaponType) {
      case WEAPON_TYPES.KNIFE:
        // Scale the knife appropriately
        const knifeScale = 1.5;
        weaponModel.scale.set(knifeScale, knifeScale, knifeScale);
        break;
        
      case WEAPON_TYPES.PISTOL:
        // Scale the pistol appropriately
        const pistolScale = 2.4;
        weaponModel.scale.set(pistolScale, pistolScale, pistolScale);
        break;
        
      case WEAPON_TYPES.MACHINE_GUN:
        // Scale the rifle appropriately - increased from original
        const rifleScale = 2.0;
        weaponModel.scale.set(rifleScale, rifleScale, rifleScale);
        break;
    }
  }
  
  // New method to apply weapon-specific position adjustments
  applyWeaponPositionAdjustments(weaponType, weaponContainer, weaponModel) {
    switch(weaponType) {
      case WEAPON_TYPES.KNIFE:
        // Apply fixed rotations for knife
        weaponContainer.rotateX(Math.PI * 0.4);   // Pitch adjustment
        weaponContainer.rotateY(Math.PI * 0.1);   // Yaw adjustment
        weaponContainer.rotateZ(-Math.PI * 0.5);  // Roll adjustment
        
        // Apply position offsets
        weaponContainer.translateX(0.05);   // Right/left
        weaponContainer.translateY(-0.05);  // Up/down
        weaponContainer.translateZ(0.15);   // Forward/back
        
        // Model-specific rotation
        weaponModel.rotation.set(Math.PI * 0.1, Math.PI * 0.05, -Math.PI * 0.1);
        break;
        
      case WEAPON_TYPES.PISTOL:
        // Apply fixed rotations for pistol
        weaponContainer.rotateX(Math.PI * 0.4);  // Pitch adjustment
        weaponContainer.rotateY(Math.PI * 0.02);   // Yaw adjustment
        weaponContainer.rotateZ(-Math.PI * 0.4);  // Roll adjustment
        
        // Apply position offsets
        weaponContainer.translateX(0.04);   // Right/left
        weaponContainer.translateY(0.3);  // Up/down
        weaponContainer.translateZ(0.14);   // Forward/back
        
        // Model-specific rotation
        weaponModel.rotation.set(Math.PI * 0.05, Math.PI * 0.05, -Math.PI * 0.05);
        break;
        
      case WEAPON_TYPES.MACHINE_GUN:
        // Same as the original rifle implementation
        weaponContainer.rotateX(Math.PI * 0.48);  // Pitch adjustment
        weaponContainer.rotateY(Math.PI * 0.1);   // Yaw adjustment 
        weaponContainer.rotateZ(-Math.PI * 0.5);  // Roll adjustment
        
        // Apply position offsets
        weaponContainer.translateX(0.03);   // Right/left
        weaponContainer.translateY(-0.06);  // Up/down
        weaponContainer.translateZ(0.20);   // Forward/back
        
        // Model-specific rotation
        weaponModel.rotation.set(Math.PI * 0.10, Math.PI * 0.03, -Math.PI * 0.08);
        break;
    }
  }
  
  // Add a new method to update recoil recovery
  updateRecoil(deltaTime = 16) {
    // If no recoil, skip
    if (this.recoilState.current <= 0) {
      return;
    }
    
    // Calculate time-based recovery
    const now = Date.now();
    if (!this.recoilState.lastUpdate) {
      this.recoilState.lastUpdate = now;
      return;
    }
    
    // Check if it's been more than 1.5 seconds since last shot to reset shotsFired counter
    if (now - this.state.lastShotTime > 1500) {
      this.recoilState.shotsFired = 0;
    }
    
    // Reduce recoil based on time passed since last update
    const elapsed = Math.min((now - this.recoilState.lastUpdate) / 1000, 0.1); // Cap at 100ms to prevent huge jumps
    this.recoilState.current -= this.recoilState.recovery * (elapsed * 60); // Normalize to 60fps
    
    // Don't go below zero
    if (this.recoilState.current < 0) {
      this.recoilState.current = 0;
    }
    
    this.recoilState.lastUpdate = now;
  }

  // Add method to update timing statistics
  updateTimingStats(totalTime, traverseTime, intersectTime, processTime) {
    // Keep only the last 10 timings for moving average
    const maxSamples = 10;
    
    // Update total time array
    this.hitStats.lastTotalTimes.push(totalTime);
    if (this.hitStats.lastTotalTimes.length > maxSamples) {
      this.hitStats.lastTotalTimes.shift();
    }
    
    // Update traverse time array
    this.hitStats.lastTraverseTimes.push(traverseTime);
    if (this.hitStats.lastTraverseTimes.length > maxSamples) {
      this.hitStats.lastTraverseTimes.shift();
    }
    
    // Update intersect time array
    this.hitStats.lastIntersectTimes.push(intersectTime);
    if (this.hitStats.lastIntersectTimes.length > maxSamples) {
      this.hitStats.lastIntersectTimes.shift();
    }
    
    // Update process time array
    this.hitStats.lastProcessTimes.push(processTime);
    if (this.hitStats.lastProcessTimes.length > maxSamples) {
      this.hitStats.lastProcessTimes.shift();
    }
    
    // Calculate moving averages
    this.hitStats.averageTotalTime = this.calculateAverage(this.hitStats.lastTotalTimes);
    this.hitStats.averageTraverseTime = this.calculateAverage(this.hitStats.lastTraverseTimes);
    this.hitStats.averageIntersectTime = this.calculateAverage(this.hitStats.lastIntersectTimes);
    this.hitStats.averageProcessTime = this.calculateAverage(this.hitStats.lastProcessTimes);
  }

  // Helper method to calculate average of an array
  calculateAverage(array) {
    if (array.length === 0) return 0;
    const sum = array.reduce((a, b) => a + b, 0);
    return sum / array.length;
  }

  // Create a visual effect at the hit point with color based on body part
  createHitEffect(position, bodyPart = 'default') {
    // Different colors for different body parts
    const hitColors = {
      head: 0xff0000,    // Red for headshots
      torso: 0xffff00,   // Yellow for torso
      limb: 0x00ff00,    // Green for limbs
      hand: 0x0000ff,    // Blue for extremities
      default: 0xffffff  // White for other hits
    };
    
    // Get color based on body part
    const hitColor = hitColors[bodyPart] || hitColors.default;
    
    // Create a simple particle effect at the hit position
    const particleGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const particleMaterial = new THREE.MeshBasicMaterial({ 
      color: hitColor,
      transparent: true,
      opacity: 0.8
    });
    
    const particle = new THREE.Mesh(particleGeometry, particleMaterial);
    particle.position.copy(position);
    this.scene.add(particle);
    
    // Make the particle size depend on the body part
    // Headshots get a larger effect
    if (bodyPart === 'head') {
      particle.scale.multiplyScalar(1.5);
    }
    
    // Add text label for body part (for debugging)
    if (bodyPart !== 'default') {
      // Only show in debug mode (can be toggled)
      const showDebugLabels = false;
      
      if (showDebugLabels) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 64;
        canvas.height = 32;
        context.fillStyle = "#ffffff";
        context.font = "bold 12px Arial";
        context.fillText(bodyPart.toUpperCase(), 5, 20);
        
        const texture = new THREE.CanvasTexture(canvas);
        const labelMaterial = new THREE.SpriteMaterial({
          map: texture,
          transparent: true
        });
        
        const label = new THREE.Sprite(labelMaterial);
        label.position.copy(position);
        label.position.y += 0.2; // Position above hit point
        label.scale.set(0.3, 0.15, 1);
        this.scene.add(label);
        
        // Remove the label after the same delay
        setTimeout(() => {
          this.scene.remove(label);
          labelMaterial.dispose();
          texture.dispose();
        }, 800);
      }
    }
    
    // Remove the particle after a short time
    setTimeout(() => {
      this.scene.remove(particle);
      particleGeometry.dispose();
      particleMaterial.dispose();
    }, 300);
  }
  
  // Set the sound system volume
  setSoundVolume(volume) {
    // Clamp volume between 0 and 1
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.soundSystem.volume = clampedVolume;
    
    // Update volume for all loaded sounds
    Object.values(this.soundSystem.sounds).forEach(sound => {
      sound.volume = clampedVolume;
    });
    
    console.log(`Set weapon sound volume to ${clampedVolume}`);
    return clampedVolume;
  }
  
  // Toggle weapon sounds on/off
  toggleSounds(enabled = null) {
    // Toggle if no value provided
    if (enabled === null) {
      this.soundSystem.enabled = !this.soundSystem.enabled;
    } else {
      this.soundSystem.enabled = !!enabled;
    }
    
    console.log(`Weapon sounds ${this.soundSystem.enabled ? 'enabled' : 'disabled'}`);
    return this.soundSystem.enabled;
  }
}

export default WeaponSystem; 